/** The tabs view's layout (plan D1, §2 Facet column, §5): the registry and one booking →
 *  each facet tab's sections, tabs in `BOOKING_RECORD_FACETS` order. Pure — no draft
 *  state; the panels' FieldSlots read the draft themselves.
 *
 *  A facet is laid out the way the spine lays out the whole registry (`spineSections`
 *  over the facet's part of it): one section per stage the facet reaches, each split into
 *  its Editable, "From <restech>" and System blocks, the party rows under the guest's
 *  stage. So a field sits in the same block, in the same order, rendered by the same stage
 *  panel in both views — Booking holds Booked & order, Departure and After trip; Guest is
 *  the whole Guest & pre-check-in panel. A section that keeps its stage's editable fields
 *  keeps the stage's title; a read-only part of a stage (Money, Integration, Activity) is
 *  titled by its tab. Every catalog key lands on exactly one tab (the contract test). */
import { BOOKING_RECORD_FACETS, type BookingRecordFacet, type BookingRecordStage } from '@/api/bookingRecord';
import type { RecordViewDef, ViewSection } from '@/features/records/views/types';

import { FACET_TITLES, facetRegistry, type BookingRegistry } from '../registry';

import { spineSections, type BookingRecordData } from './spineLayout';

export interface FacetSection extends ViewSection<BookingRecordStage> {
  facet: BookingRecordFacet;
}

/** The DOM ids of a facet's tab and its panel (`aria-controls` / `aria-labelledby`). */
export const facetTabId = (facet: BookingRecordFacet) => `booking-facet-tab-${facet}`;
export const facetPanelId = (facet: BookingRecordFacet) => `booking-facet-panel-${facet}`;

export function tabsSections(registry: BookingRegistry, data: BookingRecordData): FacetSection[] {
  return BOOKING_RECORD_FACETS.flatMap((facet) =>
    spineSections(facetRegistry(registry, facet), data)
      .filter((s) => s.blocks.length > 0)
      .map((s) => ({
        ...s,
        facet,
        title: s.blocks.some((b) => b.kind === 'editable') ? s.title : FACET_TITLES[facet],
      })),
  );
}

export const TABS_VIEW: RecordViewDef<BookingRegistry, BookingRecordData, BookingRecordStage> = {
  id: 'tabs',
  label: 'Tabs',
  layout: tabsSections,
};
